/**
 * Where a chat lives. The same ChatPage serves plain chats (/chat) and Build
 * with AI chats (/build/with-ai): the host decides the URLs, the history page
 * and the `source` a new chat is created with (docs/design/web/WEB_BUILD_PATTERN_PLAN.md §3).
 */
export interface ChatHost {
  kind: "chat" | "build";
  /** Sent on POST /chat/sessions; omitted for plain chats (the server default). */
  source?: "build";
  newPath: string;
  historyPath: string;
  pathFor: (sessionUuid: string) => string;
}

export const chatHost: ChatHost = {
  kind: "chat",
  newPath: "/chat",
  historyPath: "/chats",
  pathFor: (uuid) => `/chat/${uuid}`,
};

export const buildHost: ChatHost = {
  kind: "build",
  source: "build",
  newPath: "/build/with-ai",
  historyPath: "/build/with-ai/history",
  pathFor: (uuid) => `/build/with-ai/${uuid}`,
};

/** The host a saved chat belongs to, from its `source`. */
export const hostFor = (source: string | null | undefined): ChatHost => (source === "build" ? buildHost : chatHost);

/** Where a saved chat opens when it belongs to the other host (a build chat found under /chat, or the reverse). */
export function otherHostPath(host: ChatHost, chat: { uuid: string; source?: string } | undefined): string | null {
  if (!chat) return null;
  const own = hostFor(chat.source);
  return own.kind === host.kind ? null : own.pathFor(chat.uuid);
}

/** A new chat's agent and space: the ones picked, or none for a build chat (the default assistant, no space). */
export function newChatScope(isBuild: boolean, pickedAgent: string | null, space: string | null) {
  return isBuild ? { agentUuid: null, spaceUuid: null } : { agentUuid: pickedAgent, spaceUuid: space };
}

interface Scope {
  agentUuid: string | null;
  spaceUuid: string | null;
}

/** The chat's agent and space: an open chat's own, else the new chat's. */
export function chatScope(
  sessionUuid: string | undefined,
  saved: { agentUuid?: string | null; spaceUuid?: string | null } | undefined,
  fresh: Scope,
): Scope {
  if (!sessionUuid) return fresh;
  return { agentUuid: saved?.agentUuid ?? null, spaceUuid: saved?.spaceUuid ?? null };
}
